"use client";
import { ProcessedCourse } from "@/common/course/types";
import Image from "next/image";
import Highlights from "@/components/Highlights";
import { IconTypes } from "@/components/icons/types";
import { HighlightItem } from "@/components/Highlights/HighlightsItem";
import useTranslations from "@/i18n/useTranslations";

// Utility function to map ProgramFacts data to Highlights format
const mapFactsToHighlights = (
  facts?: {
    title?: string;
    description?: string;
    icon?: string;
  }[],
): HighlightItem[] => {
  if (!facts) return [];

  return facts.map((fact) => ({
    iconName: (fact.icon as IconTypes) || "certificate",
    title: fact.title || "",
    subtitle: fact.description || "",
    showPartnerName: false,
  }));
};

interface WhoShouldTakeProps {
  course: ProcessedCourse;
  facts?: {
    title?: string;
    description?: string;
    icon?: string;
  }[];
  factsTitle?: string;
  title?: string;
  description?: string;
  imageUrl?: string;
}

export default function WhoShouldTake({
  facts,
  factsTitle,
  title,
  description,
  imageUrl,
}: WhoShouldTakeProps) {
  const t = useTranslations("Common");
  const highlightItems = mapFactsToHighlights(facts);
  return (
    <>
      <section id="who-should-take">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
          <div className="flex-1">
            <h2 className="text-3xl font-bold mb-6 text-balance">{title}</h2>
            <div className="space-y-4 text-foreground/80 leading-relaxed">
              <p>{description}</p>
            </div>
          </div>
          {imageUrl && (
            <div className="flex-shrink-0 w-full lg:w-[400px]">
              <Image
                src={imageUrl}
                alt={t("whoShouldTakeAlt")}
                width={400}
                height={300}
                className="rounded-[10px] w-full h-auto"
              />
            </div>
          )}
        </div>
      </section>
      {highlightItems.length > 0 && (
        <div className="pt-12">
          <Highlights
            title={factsTitle}
            items={highlightItems}
            className="bg-transparent"
            containerClassName="bg-transparent"
            highlightsContainerClassName="bg-white border border-border"
          />
        </div>
      )}
    </>
  );
}
